import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { initialData } from '../../../../../constant/location';
import { FormTextField, ReactSelectForm } from '../../../../../components';
import { CourtSection } from '../CourtSection';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { updateTournamentInfo } from '../../../../../features/tournament/actionCreators';
import { locationTypeConverter } from '../../../../../utils/locationTypeConverter';
import { TagIdsEnum } from '../../../../../constant/navigation';
import { useParams } from 'react-router-dom';
import countries from 'i18n-iso-countries';
import { getTimezoneOptions, getTimezoneForCountry } from '../../../../../utils/timezone';
import { getScheduleConstraints, getTournamentSchedule } from '../../../../../apis/tournament';
import {
  buildTimezoneMigrationPreview,
  countScheduledMatches,
  hasStoredTimezoneChanged,
  needsLocationTimezoneMigration,
  requiresTimezoneMigrationPreview,
  resolveLocationTimezoneMigrationPrevious,
  shouldBlockTimezoneChangeWhenPreviewUnavailable,
  type TimezoneMigrationPreviewLine,
} from '../../../../../features/schedule/scheduleTimezone';
import { getTournamentTimezone } from '../../../Schedule/components/schedule.helpers';
import { isFreeSlot, type IMatchScheduleItem } from '../../../../../types/schedule';
import { Notification } from '../../../../../hooks/useNotification';
import { TypeError } from '../../../../../types/enums';
import { TimezoneChangeConfirmModal } from '../TimezoneChangeConfirmModal';

export type DataCourts = {
  readonly id?: string | number;
  name: string;
  isEdit?: boolean;
};

export interface LocationValues {
  location: string;
  pinLocation: string | null;
  lat: number;
  lng: number;
  courtCount: number;
  courts: Array<DataCourts>;
  country?: string;
  timeZone?: string;
}

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
countries.registerLocale(require('i18n-iso-countries/langs/sv.json'));
countries.registerLocale(require('i18n-iso-countries/langs/fr.json'));
countries.registerLocale(require('i18n-iso-countries/langs/es.json'));
countries.registerLocale(require('i18n-iso-countries/langs/it.json'));
countries.registerLocale(require('i18n-iso-countries/langs/pt.json'));

export const LocationTag = () => {
  const { t, i18n } = useTranslation();
  const prefix = 'creativeDetail-';
  const inputPrefix = 'input-';
  const prefixValidation = 'validation-';
  const { communityId } = useParams<{ communityId: string }>();
  const listCountries = useMemo(() => {
    const language = i18n.language.substring(0, 2);
    const countriesi18n = countries.getNames(language, { select: 'alias' });
    return Object.entries(countriesi18n)
      .map(([code, name]) => ({
        value: code,
        label: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [i18n.language]);

  const LocationSchema = Yup.object().shape({
    courtCount: Yup.number()
      .typeError(t(prefixValidation + 'mustBeANumber'))
      .positive(t(prefixValidation + 'positive')),
    timeZone: Yup.string().required(t(prefixValidation + 'required')),
  });
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state) => state.tournament);
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const formikInitialState = data
    ? {
        location: data.location || '',
        pinLocation: data.pinLocation || '',
        lat: data.lat,
        lng: data.lng,
        courtCount: data.courtCount || 1,
        courts: data.courts.map((court) => ({ ...court, isEdit: true })),
        country: data.country || '',
        timeZone: (data as any).timeZone || '',
      }
    : { ...initialData };

  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [pendingLocationValues, setPendingLocationValues] = useState<LocationValues | null>(null);
  const [previewLines, setPreviewLines] = useState<TimezoneMigrationPreviewLine[]>([]);
  const [unscheduledMigrationCount, setUnscheduledMigrationCount] = useState(0);
  const [scheduledMatchCount, setScheduledMatchCount] = useState(0);
  const [isSubmittingTimezone, setIsSubmittingTimezone] = useState(false);
  const [oldTimeZoneLabel, setOldTimeZoneLabel] = useState('');
  const [newTimeZoneLabel, setNewTimeZoneLabel] = useState('');
  const [pendingMigrateFromTimeZone, setPendingMigrateFromTimeZone] = useState<string | null>(
    null
  );

  const resolveTimezoneLabel = useCallback(
    (tz: string) => timezoneOptions.find((opt) => opt.value === tz)?.label ?? tz,
    [timezoneOptions]
  );

  const submitLocation = useCallback(
    async (
      values: LocationValues,
      migrateScheduleTimezone = false,
      migrateFromTimeZone?: string
    ) => {
      if (!data) return;
      setIsSubmittingTimezone(true);
      try {
        const payload = {
          ...locationTypeConverter(values),
          ...(migrateScheduleTimezone
            ? {
                migrateScheduleTimezone: true,
                ...(migrateFromTimeZone ? { migrateFromTimeZone } : {}),
              }
            : {}),
        };
        await dispatch(
          updateTournamentInfo(data.id, +communityId!, payload, undefined, {
            refetchScheduleAfterTimezoneMigration: migrateScheduleTimezone,
          })
        );
        setShowTimezoneModal(false);
        setPendingLocationValues(null);
        setPendingMigrateFromTimeZone(null);
      } finally {
        setIsSubmittingTimezone(false);
      }
    },
    [communityId, data, dispatch]
  );

  const handleSubmit = async (values: LocationValues) => {
    if (!data) return;

    const selectedTimeZone = values.timeZone ?? '';
    const timezoneChanged = hasStoredTimezoneChanged(data.timeZone, values.timeZone);

    if (!timezoneChanged) {
      await submitLocation(values, false);
      return;
    }

    const hasTournamentDates = Boolean(data.startDate && data.endDate);
    const countryFallbackTimeZone = getTournamentTimezone(data.country, data.timeZone);

    let constraints: Awaited<ReturnType<typeof getScheduleConstraints>> | null = null;
    let schedulePayload: Awaited<ReturnType<typeof getTournamentSchedule>> | null = null;
    let constraintsFetchFailed = false;
    let scheduleFetchFailed = false;

    try {
      constraints = await getScheduleConstraints(data.id);
    } catch {
      constraintsFetchFailed = true;
    }

    try {
      schedulePayload = await getTournamentSchedule(data.id);
    } catch {
      scheduleFetchFailed = true;
    }

    const scheduledMatches = (schedulePayload?.schedule?.days ?? []).flatMap((day) =>
      day.courts.flatMap((court) =>
        court.matches.filter((match): match is IMatchScheduleItem => !isFreeSlot(match))
      )
    );

    const matchCount = countScheduledMatches(scheduledMatches);
    const hasConstraints = Boolean(constraints?.days?.length);
    const resolvedPreviousTimeZone = resolveLocationTimezoneMigrationPrevious(
      data.timeZone,
      constraints?.scheduleTimezone,
      countryFallbackTimeZone
    );
    const needsMigration = needsLocationTimezoneMigration(
      data.timeZone,
      selectedTimeZone,
      resolvedPreviousTimeZone
    );

    if (!needsMigration) {
      await submitLocation(values, false);
      return;
    }

    const requiresPreview = requiresTimezoneMigrationPreview({
      hasTournamentDates,
      hasConstraints,
      isScheduled: Boolean(data.isScheduled),
      matchCount,
    });

    if (
      shouldBlockTimezoneChangeWhenPreviewUnavailable(
        requiresPreview,
        constraintsFetchFailed,
        scheduleFetchFailed
      )
    ) {
      Notification(TypeError.error, t('creativeDetail-locationTimezonePreviewError'), 'bottom-right');
      return;
    }

    if (!requiresPreview) {
      await submitLocation(values, false);
      return;
    }

    try {
      const migrationPreview = buildTimezoneMigrationPreview(
          constraints,
          scheduledMatches,
          resolvedPreviousTimeZone,
          selectedTimeZone,
          hasTournamentDates
            ? { startDate: data.startDate!, endDate: data.endDate! }
            : undefined
        );
      setPreviewLines(migrationPreview.lines);
      setUnscheduledMigrationCount(migrationPreview.unscheduledCount);
      setScheduledMatchCount(matchCount);
      setOldTimeZoneLabel(resolveTimezoneLabel(resolvedPreviousTimeZone));
      setNewTimeZoneLabel(resolveTimezoneLabel(selectedTimeZone));
      setPendingLocationValues(values);
      setPendingMigrateFromTimeZone(resolvedPreviousTimeZone);
      setShowTimezoneModal(true);
    } catch {
      Notification(TypeError.error, t('creativeDetail-locationTimezonePreviewError'), 'bottom-right');
    }
  };

  return (
    <div className="px-2">
      <Formik
        enableReinitialize={true}
        validationSchema={LocationSchema}
        initialValues={formikInitialState}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form autoComplete="off" id={TagIdsEnum.LOCATION}>
            <div className="my-3">
              <h4 className="font-weight-bold">{t(prefix + 'address')}</h4>
            </div>
            <div className="row">
              <div className="col-lg-4 d-sm-block">
                <FormTextField
                  type="text"
                  maxLength={50}
                  placeholder={t(inputPrefix + 'placeholderName')}
                  id="location"
                  label={t(prefix + 'name')}
                  name="location"
                />
                <FormTextField
                  type="text"
                  maxLength={50}
                  placeholder={t(inputPrefix + 'placeholderPlace')}
                  id="pinLocation"
                  label={t(prefix + 'pin')}
                  name="pinLocation"
                />
                <ReactSelectForm
                  preselect={false}
                  defaultValue={''}
                  options={listCountries}
                  label={t(prefix + 'country')}
                  name="country"
                  onChangeCb={(code: string) => {
                    if (!values.timeZone) {
                      const guessed = getTimezoneForCountry(code);
                      if (guessed) setFieldValue('timeZone', guessed);
                    }
                  }}
                />
                <ReactSelectForm
                  preselect={false}
                  defaultValue={''}
                  options={timezoneOptions}
                  label={t(prefix + 'timeZone')}
                  name="timeZone"
                />
              </div>
            </div>
            <CourtSection
              data={{
                dataCourts: values.courts,
                courtAmount: values.courtCount,
              }}
            />
          </Form>
        )}
      </Formik>
      <TimezoneChangeConfirmModal
        show={showTimezoneModal}
        oldTimeZone={oldTimeZoneLabel}
        newTimeZone={newTimeZoneLabel}
        scheduledMatchCount={scheduledMatchCount}
        unscheduledMigrationCount={unscheduledMigrationCount}
        previewLines={previewLines}
        isSubmitting={isSubmittingTimezone}
        onCancel={() => {
          setShowTimezoneModal(false);
          setPendingLocationValues(null);
          setPendingMigrateFromTimeZone(null);
        }}
        onConfirm={() => {
          if (pendingLocationValues) {
            void submitLocation(
              pendingLocationValues,
              true,
              pendingMigrateFromTimeZone ?? undefined
            );
          }
        }}
      />
    </div>
  );
};
